import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Mountain, Tent, Compass, UtensilsCrossed,
  Sun, Sparkles, BookOpen, Camera, Star, Building,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PageStickyNav from "@/components/PageStickyNav";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const HERO = "https://images.unsplash.com/photo-1489493512598-d08130f49bea?auto=format&fit=crop&w=2400&q=80";

const REGIONS = [
  {
    id: "merzouga",
    icon: Tent,
    name: { en: "Merzouga", fr: "Merzouga", es: "Merzouga" },
    subtitle: { en: "Gateway to Erg Chebbi", fr: "La porte de l'Erg Chebbi", es: "La puerta del Erg Chebbi" },
    text: {
      en: "A small Saharan village at the very foot of the Erg Chebbi — Morocco's most cinematic sand sea, with dunes rising 150 metres above the hammada. Camel caravans leave here at sunset, and bivouac fires light the night.",
      fr: "Un petit village saharien au pied même de l'Erg Chebbi — la mer de sable la plus cinématographique du Maroc, avec des dunes qui culminent à 150 mètres au-dessus de la hammada. Les caravanes chamelières partent au coucher du soleil, les feux de bivouac s'allument à la nuit tombée.",
      es: "Un pequeño pueblo sahariano al pie mismo del Erg Chebbi — el mar de arena más cinematográfico de Marruecos, con dunas que se elevan 150 metros sobre la hammada. Las caravanas en camello salen al atardecer, las hogueras del bivac encienden la noche.",
    },
    image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
    badges: [
      { en: "Sahara desert", fr: "Désert du Sahara", es: "Desierto del Sáhara" },
      { en: "Camel caravans", fr: "Caravanes chamelières", es: "Caravanas en camello" },
      { en: "Dune bivouacs", fr: "Bivouacs en dune", es: "Vivacs en duna" },
    ],
  },
  {
    id: "rissani",
    icon: BookOpen,
    name: { en: "Rissani", fr: "Rissani", es: "Rissani" },
    subtitle: { en: "Cradle of the Alaouite dynasty", fr: "Berceau de la dynastie alaouite", es: "Cuna de la dinastía alauita" },
    text: {
      en: "A historical caravan town near the dunes — host to the Mausoleum of Moulay Ali Cherif, founder of the ruling Alaouite dynasty. Its Sunday souk is one of the most authentic in the south.",
      fr: "Une ville-caravansérail historique près des dunes — abrite le mausolée de Moulay Ali Cherif, fondateur de la dynastie alaouite. Son souk du dimanche est l'un des plus authentiques du sud.",
      es: "Una histórica ciudad caravanera cerca de las dunas — alberga el mausoleo de Mulay Alí Cherif, fundador de la dinastía alauita. Su zoco del domingo es uno de los más auténticos del sur.",
    },
    image: "https://images.unsplash.com/photo-1539020140153-e479b8c5dfd2?auto=format&fit=crop&w=1600&q=80",
    badges: [
      { en: "Royal mausoleum", fr: "Mausolée royal", es: "Mausoleo real" },
      { en: "Sunday souk", fr: "Souk du dimanche", es: "Zoco del domingo" },
      { en: "Tafilalet oasis", fr: "Oasis du Tafilalet", es: "Oasis del Tafilalet" },
    ],
  },
  {
    id: "arfoud",
    icon: Sun,
    name: { en: "Arfoud", fr: "Arfoud", es: "Arfoud" },
    subtitle: { en: "The door to the desert", fr: "La porte du désert", es: "La puerta del desierto" },
    text: {
      en: "An oasis town in the Tafilalet, the natural staging-base for travellers heading to Erg Chebbi. Famous for its date palms, its fossil-marble workshops and its hospitality infrastructure — including the founding Kasbah Hotel Xaluca.",
      fr: "Une ville-oasis du Tafilalet, base logistique naturelle pour les voyageurs en route vers l'Erg Chebbi. Réputée pour ses palmiers-dattiers, ses ateliers de marbre fossile et son infrastructure d'accueil — dont la Kasbah Hotel Xaluca fondatrice.",
      es: "Una ciudad-oasis del Tafilalet, base logística natural para los viajeros que se dirigen al Erg Chebbi. Famosa por sus palmeras datileras, sus talleres de mármol fósil y su infraestructura de acogida — entre ellas la fundacional Kasbah Hotel Xaluca.",
    },
    image: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg",
    badges: [
      { en: "Oasis town", fr: "Ville-oasis", es: "Ciudad-oasis" },
      { en: "Fossil marble", fr: "Marbre fossile", es: "Mármol fósil" },
      { en: "Date palms", fr: "Palmiers dattiers", es: "Palmeras datileras" },
    ],
  },
  {
    id: "todgha",
    icon: Mountain,
    name: { en: "Todgha Gorges", fr: "Gorges du Todgha", es: "Gargantas del Todgha" },
    subtitle: { en: "300-metre limestone cliffs", fr: "Falaises calcaires de 300 m", es: "Acantilados calcáreos de 300 m" },
    text: {
      en: "A 600-metre-deep canyon carved by the Todgha river through the eastern High Atlas — sheer 300-metre cliffs squeezed into a corridor barely 10 metres wide. A paradise for climbers, palm-grove walkers and silence-seekers.",
      fr: "Un canyon de 600 mètres de profondeur creusé par le Todgha à travers le Haut Atlas oriental — falaises de 300 m resserrées sur un couloir d'à peine 10 m. Paradis des grimpeurs, des promeneurs de palmeraie et des amateurs de silence.",
      es: "Un cañón de 600 metros de profundidad excavado por el Todgha a través del Alto Atlas oriental — acantilados de 300 m que se estrechan en un corredor de apenas 10 m. Paraíso de escaladores, paseantes de palmeral y amantes del silencio.",
    },
    image: "https://images.unsplash.com/photo-1614436163996-25cee5f54290?auto=format&fit=crop&w=1600&q=80",
    badges: [
      { en: "High Atlas", fr: "Haut Atlas", es: "Alto Atlas" },
      { en: "Climbing paradise", fr: "Paradis des grimpeurs", es: "Paraíso de la escalada" },
      { en: "Palm-grove trails", fr: "Sentiers en palmeraie", es: "Senderos en palmeral" },
    ],
  },
  {
    id: "dades",
    icon: Compass,
    name: { en: "Dadès Gorges", fr: "Gorges du Dadès", es: "Gargantas del Dadès" },
    subtitle: { en: "Red canyons & monkey-finger rocks", fr: "Canyons rouges & doigts de singe", es: "Cañones rojos y dedos de mono" },
    text: {
      en: "Red sandstone canyons folded by erosion along the route of the thousand kasbahs — famous for the monkey-finger rock formations and a 365-hairpin road. Historically a stronghold of Berber kasbah architecture, dotted with adobe villages still inhabited today.",
      fr: "Canyons de grès rouge sculptés par l'érosion sur la route des mille kasbahs — célèbres pour les « doigts de singe » et une route aux 365 lacets. Historiquement bastion de l'architecture kasbah berbère, parsemés de villages d'adobe encore habités.",
      es: "Cañones de arenisca roja esculpidos por la erosión en la ruta de las mil kasbahs — famosos por los «dedos de mono» y una carretera de 365 curvas cerradas. Históricamente bastión de la arquitectura kasbah bereber, salpicados de pueblos de adobe aún habitados.",
    },
    image: "https://images.unsplash.com/photo-1565689876697-e467b6c54da2?auto=format&fit=crop&w=1600&q=80",
    badges: [
      { en: "Berber kasbahs", fr: "Kasbahs berbères", es: "Kasbahs bereberes" },
      { en: "Monkey fingers", fr: "Doigts de singe", es: "Dedos de mono" },
      { en: "Route of 1 000 kasbahs", fr: "Route des 1 000 kasbahs", es: "Ruta de las 1 000 kasbahs" },
    ],
  },
  {
    id: "boumalne",
    icon: Building,
    name: { en: "Boulmane Dades", fr: "Boumalne Dadès", es: "Boumalne Dadès" },
    subtitle: { en: "Where the Atlas meets the hammada", fr: "Où l'Atlas rencontre la hammada", es: "Donde el Atlas se encuentra con la hammada" },
    text: {
      en: "A small town at the edge of a desert plateau, at the outlet of the upper Dadès Valley — the meeting point of Atlas snowmelt and Saharan stone. Our Hotel Xaluca Dades sits on this exact threshold, with views to both worlds.",
      fr: "Une petite ville à la lisière d'un plateau désertique, à la sortie de la haute vallée du Dadès — le point de rencontre entre la fonte de l'Atlas et la pierre saharienne. Notre Hôtel Xaluca Dades se dresse sur ce seuil même, avec vue sur les deux mondes.",
      es: "Una pequeña ciudad al borde de una meseta desértica, en la salida del alto valle del Dadès — el punto de encuentro entre el deshielo del Atlas y la piedra sahariana. Nuestro Hotel Xaluca Dades se levanta justo en este umbral, con vistas a ambos mundos.",
    },
    image: "https://images.unsplash.com/photo-1612257999691-c5a39f7d8b6e?auto=format&fit=crop&w=1600&q=80",
    badges: [
      { en: "1 612 m altitude", fr: "1 612 m d'altitude", es: "1 612 m de altitud" },
      { en: "Bird-watching valley", fr: "Vallée des oiseaux", es: "Valle de pájaros" },
      { en: "Atlas gateway", fr: "Porte de l'Atlas", es: "Puerta del Atlas" },
    ],
  },
  {
    id: "roses",
    icon: Sparkles,
    name: { en: "Valley of Roses", fr: "Vallée des Roses", es: "Valle de las Rosas" },
    subtitle: { en: "Atlas foothills · M'Goun river", fr: "Contreforts de l'Atlas · oued M'Goun", es: "Estribaciones del Atlas · río M'Goun" },
    text: {
      en: "A long ribbon of villages at the foot of the High Atlas, irrigated by the Asif M'Goun river. In May, hundreds of millions of Damascena rose petals are hand-harvested at dawn for rose water and rose oil — and the village of Kelaat M'Gouna throws its legendary Rose Festival.",
      fr: "Un long ruban de villages au pied du Haut Atlas, irrigué par l'oued M'Goun. En mai, des centaines de millions de pétales de roses Damascena sont récoltés à la main à l'aube pour l'eau et l'huile de rose — et le village de Kelaat M'Gouna fête son légendaire Festival des Roses.",
      es: "Una larga cinta de pueblos al pie del Alto Atlas, irrigada por el río M'Goun. En mayo, cientos de millones de pétalos de rosa Damascena se recogen a mano al alba para agua y aceite de rosas — y el pueblo de Kelaat M'Gouna celebra su legendario Festival de las Rosas.",
    },
    image: "https://images.unsplash.com/photo-1465821185615-20b3c2fbf41b?auto=format&fit=crop&w=1600&q=80",
    badges: [
      { en: "Rose harvest in May", fr: "Récolte des roses en mai", es: "Cosecha de rosas en mayo" },
      { en: "Berber villages", fr: "Villages berbères", es: "Pueblos bereberes" },
      { en: "Rose-water artisans", fr: "Artisans de l'eau de rose", es: "Artesanos del agua de rosa" },
    ],
  },
  {
    id: "ouarzazate",
    icon: Camera,
    name: { en: "Ouarzazate", fr: "Ouarzazate", es: "Ouarzazate" },
    subtitle: { en: "The door of the desert · film city", fr: "Porte du désert · cité du cinéma", es: "Puerta del desierto · ciudad del cine" },
    text: {
      en: "A city of kasbahs and Berber inhabitants — the southern hub from which every valley unfurls. Its Taourirt Kasbah is one of the finest in Morocco, and the legendary Atlas Studios next door have hosted Lawrence of Arabia, Gladiator and Game of Thrones.",
      fr: "Une ville de kasbahs et d'habitants berbères — le pivot du sud d'où s'éploient toutes les vallées. Sa Kasbah Taourirt est l'une des plus belles du Maroc, et les célèbres Atlas Studios voisins ont accueilli Lawrence d'Arabie, Gladiator et Game of Thrones.",
      es: "Una ciudad de kasbahs y habitantes bereberes — el pivote del sur desde el que se despliegan todos los valles. Su Kasbah Taourirt es una de las más bellas de Marruecos, y los legendarios Atlas Studios vecinos han recibido a Lawrence de Arabia, Gladiator y Juego de Tronos.",
    },
    image: "https://images.unsplash.com/photo-1583249598754-b7a2f59651fb?auto=format&fit=crop&w=1600&q=80",
    badges: [
      { en: "Taourirt Kasbah", fr: "Kasbah Taourirt", es: "Kasbah Taourirt" },
      { en: "Atlas Studios", fr: "Atlas Studios", es: "Atlas Studios" },
      { en: "Door of the desert", fr: "Porte du désert", es: "Puerta del desierto" },
    ],
  },
  {
    id: "aitbenhaddou",
    icon: Star,
    name: { en: "Kasbah Aït Ben Haddou", fr: "Kasbah Aït Ben Haddou", es: "Kasbah Aït Ben Haddou" },
    subtitle: { en: "UNESCO World Heritage · 11th c.", fr: "Patrimoine mondial UNESCO · XIᵉ s.", es: "Patrimonio Mundial UNESCO · s. XI" },
    text: {
      en: "A fortified earthen city on a former caravan route between the Sahara and Marrakech — a perfectly preserved ksar of adobe towers, UNESCO-listed since 1987. Backdrop of dozens of films from Lawrence of Arabia to Gladiator, and home to our intimate Xaluca Aït Ben Haddou guesthouse.",
      fr: "Une cité fortifiée en terre crue sur l'ancienne route caravanière entre le Sahara et Marrakech — un ksar de tours d'adobe parfaitement préservé, classé UNESCO depuis 1987. Décor de Lawrence d'Arabie à Gladiator, et siège de notre intime maison d'hôtes Xaluca Aït Ben Haddou.",
      es: "Una ciudad fortificada de tierra cruda en la antigua ruta caravanera entre el Sáhara y Marrakech — un ksar de torres de adobe perfectamente conservado, declarado UNESCO desde 1987. Escenario de Lawrence de Arabia o Gladiator, y sede de nuestra íntima hospedería Xaluca Aït Ben Haddou.",
    },
    image: "https://images.unsplash.com/photo-1539020140153-e479b8c5dfd2?auto=format&fit=crop&w=1600&q=80",
    badges: [
      { en: "UNESCO since 1987", fr: "UNESCO depuis 1987", es: "UNESCO desde 1987" },
      { en: "Caravan route ksar", fr: "Ksar caravanier", es: "Ksar caravanero" },
      { en: "Cinema landmark", fr: "Décor de cinéma", es: "Escenario de cine" },
    ],
  },
];

const EXPERIENCES = [
  {
    icon: Building,
    title: { en: "Discover the south's leading hotel groups", fr: "Découvrir les grands groupes hôteliers du sud", es: "Descubrir los grandes grupos hoteleros del sur" },
    text: {
      en: "From the founding Kasbah Hotel Xaluca in Arfoud to the cedar-wood retreat of Aguelmame Sidi Ali — sleep with the people who shaped southern hospitality.",
      fr: "De la fondatrice Kasbah Hotel Xaluca à Arfoud au refuge en bois de cèdre d'Aguelmame Sidi Ali — dormez chez ceux qui ont façonné l'hospitalité du sud.",
      es: "De la fundacional Kasbah Hotel Xaluca de Arfoud al refugio de cedro de Aguelmame Sidi Ali — duerma con quienes han forjado la hospitalidad del sur.",
    },
    link: "/hotels",
  },
  {
    icon: Tent,
    title: { en: "Off-the-beaten-track villages & traditional stays", fr: "Villages hors des sentiers & adresses traditionnelles", es: "Pueblos fuera de ruta y alojamientos tradicionales" },
    text: {
      en: "Spend a night in a Berber kasbah, share a couscous in a remote douar, ride a mule to a mountain refuge most maps don't even mark.",
      fr: "Passez une nuit dans une kasbah berbère, partagez un couscous dans un douar reculé, rejoignez à dos de mulet un refuge que peu de cartes signalent.",
      es: "Pase una noche en una kasbah bereber, comparta un cuscús en un duar remoto, llegue en mula a un refugio que pocos mapas señalan.",
    },
    link: "/bivouacs",
  },
  {
    icon: UtensilsCrossed,
    title: { en: "Learn the gestures of Moroccan couscous", fr: "Apprendre les gestes du couscous marocain", es: "Aprender los gestos del cuscús marroquí" },
    text: {
      en: "A morning in our kitchens with the chef — rolling semolina by hand, steaming it three times, building the broth and the seven vegetables that crown it.",
      fr: "Une matinée en cuisine avec le chef — rouler la semoule à la main, la cuire trois fois à la vapeur, monter le bouillon et les sept légumes qui le couronnent.",
      es: "Una mañana en nuestras cocinas con el chef — enrollar la sémola a mano, cocerla tres veces al vapor, montar el caldo y las siete verduras que lo coronan.",
    },
    link: "/gastronomy",
  },
  {
    icon: Compass,
    title: { en: "A camel trek across the Sahara", fr: "Une caravane à dos de chameau dans le Sahara", es: "Una caravana en camello por el Sáhara" },
    text: {
      en: "From Merzouga, ride into the great Erg Chebbi as the sun lowers; sleep in a bivouac of woven haimas, wake at dawn to silence and a sky still full of stars.",
      fr: "Depuis Merzouga, partez dans l'Erg Chebbi au coucher du soleil ; dormez sous haimas tissées, réveillez-vous à l'aube dans un silence et un ciel encore étoilés.",
      es: "Desde Merzouga, salga al Erg Chebbi cuando el sol baja; duerma bajo haimas tejidas, despierte al alba en silencio y bajo un cielo aún estrellado.",
    },
    link: "/experiences/desert",
  },
  {
    icon: Sun,
    title: { en: "Unwind on the Atlantic — Essaouira beach", fr: "Se poser sur l'Atlantique — la plage d'Essaouira", es: "Descansar en el Atlántico — la playa de Essaouira" },
    text: {
      en: "End a southern itinerary on the windswept Atlantic coast — Essaouira's medina, its trade-wind beach, its Gnawa music. Easy to combine with our Marrakech-to-the-desert routes.",
      fr: "Terminez un itinéraire sud sur la côte atlantique — la médina d'Essaouira, sa plage des alizés, sa musique Gnawa. À combiner facilement avec nos routes Marrakech-désert.",
      es: "Termine un itinerario por el sur en la costa atlántica — la medina de Essaouira, su playa de los alisios, su música Gnawa. Combinable fácilmente con nuestras rutas Marrakech-desierto.",
    },
    link: "/trips",
  },
];

const CONTRASTS = [
  { left: { en: "Ancient", fr: "Ancien", es: "Antiguo" }, right: { en: "Modern", fr: "Moderne", es: "Moderno" } },
  { left: { en: "Arab", fr: "Arabe", es: "Árabe" }, right: { en: "Berber", fr: "Berbère", es: "Bereber" } },
  { left: { en: "European", fr: "Européen", es: "Europeo" }, right: { en: "African", fr: "Africain", es: "Africano" } },
  { left: { en: "Mountain", fr: "Montagne", es: "Montaña" }, right: { en: "Desert", fr: "Désert", es: "Desierto" } },
];

const SouthernMoroccoPage = () => {
  const { lang } = useLanguage();
  return (
    <div data-testid="page-southern-morocco" className="bg-[#FDFBF7]">
      {/* HERO — multi-slide (video first, then southern Morocco landscapes) */}
      <section data-testid="southern-morocco-hero">
        <PageHero
          images={[
            HERO,
            "https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?auto=format&fit=crop&w=2400&q=80",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg",
          ]}
          alt="Southern Morocco · Grup Xaluca"
          scope="page.southern-morocco"
          overline={pick({ en: "Discover · Southern Morocco", fr: "Découvrir · Sud du Maroc", es: "Descubrir · Sur de Marruecos" }, lang)}
          title={pick({
            en: "A land of contrasts, drawn by wind, water and time.",
            fr: "Une terre de contrastes, dessinée par le vent, l'eau et le temps.",
            es: "Una tierra de contrastes, dibujada por el viento, el agua y el tiempo.",
          }, lang)}
          sub={pick({
            en: "From the medinas of Marrakech to the dunes of Erg Chebbi, the south of Morocco hides treasures that millennia of travellers have crossed mountains and seas to reach. Here is the map of the most beautiful of them.",
            fr: "Des médinas de Marrakech aux dunes de l'Erg Chebbi, le sud du Maroc cache des trésors que des millénaires de voyageurs ont franchi mers et montagnes pour atteindre. En voici la carte la plus précieuse.",
            es: "De las medinas de Marrakech a las dunas del Erg Chebbi, el sur de Marruecos esconde tesoros que milenios de viajeros han cruzado mares y montañas para alcanzar. Aquí está su mapa más preciado.",
          }, lang)}
        />
      </section>

      <PageStickyNav
        items={[
          { id: "sm-intro", label: { en: "Intro", fr: "Intro", es: "Intro" } },
          { id: "sm-regions", label: { en: "Regions", fr: "Régions", es: "Regiones" } },
          { id: "sm-experiences", label: { en: "Experiences", fr: "Expériences", es: "Experiencias" } },
          { id: "sm-history", label: { en: "History", fr: "Histoire", es: "Historia" } },
        ]}
      />

      {/* INTRODUCTION */}
      <section id="sm-intro" className="py-24 md:py-32 scroll-mt-32" data-testid="sm-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="overline">
              {pick({ en: "An introduction", fr: "Introduction", es: "Introducción" }, lang)}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
              {pick({
                en: "One country, two ecosystems, four civilisations.",
                fr: "Un pays, deux écosystèmes, quatre civilisations.",
                es: "Un país, dos ecosistemas, cuatro civilizaciones.",
              }, lang)}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-7 font-serif-x text-[#3C342D]">
            <p className="text-2xl md:text-3xl leading-[1.35] text-[#2C2621]">
              {pick({
                en: "The south of Morocco is the country distilled — palm groves born from melted snow, sand seas the size of a small country, kasbahs that look painted onto the rock, and a sky so clean the Milky Way seems to leak onto the dunes.",
                fr: "Le sud du Maroc, c'est le pays distillé — palmeraies nées de la neige fondue, mers de sable de la taille d'un petit pays, kasbahs comme peintes sur la roche, et un ciel si pur que la Voie lactée semble couler sur les dunes.",
                es: "El sur de Marruecos es el país destilado — palmerales nacidos del deshielo, mares de arena del tamaño de un pequeño país, kasbahs como pintadas sobre la roca y un cielo tan limpio que la Vía Láctea parece derramarse sobre las dunas.",
              }, lang)}
            </p>
            <p className="text-lg leading-relaxed text-[#5C5248] font-sans">
              {pick({
                en: "Travellers have crossed seas, deserts and mountains for millennia to reach it — Phoenician sailors, Roman traders, caravans of salt and gold, Andalusian musicians fleeing Granada, French painters, twentieth-century filmmakers. They all came for the same reason: nowhere else in the world do ancient and modern, Arab and Berber, European and African meet on quite the same threshold.",
                fr: "Des voyageurs traversent depuis des millénaires mers, déserts et montagnes pour l'atteindre — marins phéniciens, marchands romains, caravanes de sel et d'or, musiciens andalous fuyant Grenade, peintres français, cinéastes du XXᵉ siècle. Tous pour la même raison : nulle part ailleurs au monde l'ancien et le moderne, l'arabe et le berbère, l'européen et l'africain ne se rencontrent sur un seuil aussi exact.",
                es: "Los viajeros han cruzado durante milenios mares, desiertos y montañas para llegar — marineros fenicios, mercaderes romanos, caravanas de sal y oro, músicos andalusíes huyendo de Granada, pintores franceses, cineastas del siglo XX. Todos por la misma razón: en ningún otro lugar del mundo lo antiguo y lo moderno, lo árabe y lo bereber, lo europeo y lo africano se encuentran en un umbral tan exacto.",
              }, lang)}
            </p>

            {/* Contrasts strip */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#E8DFD2] border border-[#E8DFD2] rounded-2xl overflow-hidden">
              {CONTRASTS.map((c, i) => (
                <div key={`contrast-${c.left.en}`} className="bg-white px-4 py-5 text-center" data-testid={`sm-contrast-${i}`}>
                  <p className="font-serif-x text-base md:text-lg text-[#C16542] tracking-tight">
                    {pick(c.left, lang)} <span className="text-[#5C5248]/60 font-sans text-sm align-middle">·</span> {pick(c.right, lang)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section id="sm-regions" className="py-24 md:py-32 bg-[#FBF6EE]/70 scroll-mt-32" data-testid="sm-regions">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="overline">
                {pick({ en: "Key regions & landmarks", fr: "Régions & sites emblématiques", es: "Regiones e hitos clave" }, lang)}
              </p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
                {pick({
                  en: "Nine places that make the south.",
                  fr: "Neuf lieux qui font le sud.",
                  es: "Nueve lugares que hacen el sur.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base text-[#5C5248] leading-relaxed">
                {pick({
                  en: "From a small Saharan oasis to a UNESCO-listed ksar — every region carries a different soundtrack, a different cuisine, a different colour palette. Skim the names. Linger on the ones that pull at you.",
                  fr: "D'une petite oasis saharienne à un ksar classé UNESCO — chaque région a sa bande-son, sa cuisine, sa palette de couleurs. Lisez les noms. Attardez-vous sur ceux qui vous appellent.",
                  es: "De un pequeño oasis sahariano a un ksar declarado UNESCO — cada región tiene su banda sonora, su cocina, su paleta. Recorra los nombres. Deténgase en los que le llaman.",
                }, lang)}
              </p>
            </div>
          </div>

          <div className="space-y-10 md:space-y-14">
            {REGIONS.map((r, i) => {
              const Icon = r.icon;
              const reverse = i % 2 === 1;
              return (
                <article
                  key={r.id}
                  className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center"
                  id={r.id}
                  data-testid={`sm-region-${r.id}`}
                  data-cms-scope={`region.${r.id}`}
                >
                  <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                    <div className="aspect-[4/3] overflow-hidden rounded-sm">
                      <img src={r.image} alt={pick(r.name, lang)} className="w-full h-full object-cover" loading="lazy" decoding="async" data-cms-role="card.image"/>
                    </div>
                  </div>
                  <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full bg-[#C16542]/10 text-[#C16542] flex items-center justify-center">
                        <Icon className="w-4 h-4" strokeWidth={1.6} />
                      </span>
                      <p className="overline">{String(i + 1).padStart(2, "0")}</p>
                    </div>
                    <h3 className="font-serif-x mt-4 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#2C2621]">
                      {pick(r.name, lang)}
                    </h3>
                    <p className="mt-2 text-[12px] tracking-[0.25em] uppercase text-[#C16542]">
                      <MapPin className="w-3 h-3 inline mr-1.5" strokeWidth={1.6} />
                      {pick(r.subtitle, lang)}
                    </p>
                    <p className="mt-5 text-lg text-[#5C5248] leading-relaxed">
                      {pick(r.text, lang)}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {r.badges.map((b, j) => (
                        <span
                          key={`badge-${r.id}-${b.en}`}
                          className="text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 rounded-full bg-white border border-[#E8DFD2] text-[#2C2621]"
                        >
                          {pick(b, lang)}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CULTURAL & EXPERIENTIAL HIGHLIGHTS */}
      <section id="sm-experiences" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32" data-testid="sm-experiences">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="overline !text-[#D4A373]">
                {pick({ en: "Cultural & experiential highlights", fr: "Culture & expériences phares", es: "Cultura y experiencias destacadas" }, lang)}
              </p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick({
                  en: "Five ways to make the south yours.",
                  fr: "Cinq manières de faire vôtre le sud.",
                  es: "Cinco maneras de hacer suyo el sur.",
                }, lang)}
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXPERIENCES.map((e, i) => {
              const Icon = e.icon;
              return (
                <Link
                  key={`sm-experience-${e.link || e.title.en}`}
                  to={e.link}
                  className="group bg-[#FDFBF7]/[0.04] border border-[#FDFBF7]/15 p-7 md:p-8 rounded-3xl hover:bg-[#FDFBF7]/[0.08] hover:border-[#D4A373]/40 transition-all"
                  data-testid={`sm-experience-${i}`}
                >
                  <span className="w-11 h-11 rounded-full bg-[#D4A373]/15 text-[#D4A373] flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif-x text-2xl md:text-[26px] mt-5 leading-tight text-[#FDFBF7]">
                    {pick(e.title, lang)}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#FDFBF7]/75">
                    {pick(e.text, lang)}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#D4A373] border-b border-[#D4A373]/60 pb-0.5 w-fit group-hover:text-[#FDFBF7] group-hover:border-[#FDFBF7]/80 transition-colors">
                    {pick({ en: "Explore", fr: "Explorer", es: "Explorar" }, lang)}
                    <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HISTORICAL CONTEXT — pull quote */}
      <section id="sm-history" className="py-24 md:py-28 bg-[#F2EBE1]/50 scroll-mt-32" data-testid="sm-history">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="overline">
            {pick({ en: "A note of history", fr: "Un mot d'histoire", es: "Un apunte histórico" }, lang)}
          </p>
          <h2 className="font-serif-x mt-5 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-[#2C2621]">
            {pick({
              en: "For three thousand years, the south of Morocco has been a crossroads — Phoenicians, Romans, Berbers, Arabs, Andalusians, Saharan tribes, French settlers. Every layer is still here, in the food, in the language, in the way a host pours your tea.",
              fr: "Depuis trois mille ans, le sud du Maroc est un carrefour — Phéniciens, Romains, Berbères, Arabes, Andalous, tribus sahariennes, colons français. Toutes ces strates sont encore là, dans la cuisine, dans la langue, dans la manière dont un hôte verse votre thé.",
              es: "Durante tres mil años, el sur de Marruecos ha sido un cruce de caminos — fenicios, romanos, bereberes, árabes, andalusíes, tribus saharianas, colonos franceses. Todas esas capas siguen aquí, en la cocina, en la lengua, en la forma en que un anfitrión sirve su té.",
            }, lang)}
          </h2>
          <p className="mt-8 text-[11px] tracking-[0.3em] uppercase text-[#5C5248]">
            {pick({ en: "— Grup Xaluca editorial", fr: "— Édito Grup Xaluca", es: "— Editorial Grup Xaluca" }, lang)}
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Compass className="w-7 h-7 mx-auto text-[#D4A373]" strokeWidth={1.5} />
          <h2 className="font-serif-x mt-6 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            {pick({
              en: "Ready to compose your southern route?",
              fr: "Prêt à composer votre itinéraire du sud ?",
              es: "¿Listo para componer su ruta del sur?",
            }, lang)}
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-[#FDFBF7]/75 leading-relaxed">
            {pick({
              en: "Our concierge has lived this region for a lifetime — let us draw the line between dunes, kasbahs, lakes and rose valleys that fits you best.",
              fr: "Notre conciergerie vit cette région depuis toujours — laissez-nous tracer entre dunes, kasbahs, lacs et vallées des roses la ligne qui vous correspond.",
              es: "Nuestra conserjería ha vivido esta región toda la vida — déjenos trazar entre dunas, kasbahs, lagos y valles de rosas la línea que mejor le encaja.",
            }, lang)}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/trips"
              className="inline-flex items-center justify-center gap-2 bg-[#C16542] text-[#FDFBF7] px-7 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#D4A373] hover:text-[#1A1513] transition-colors"
              data-testid="sm-cta-trips"
            >
              {pick({ en: "Browse our trips", fr: "Voir nos voyages", es: "Ver nuestros viajes" }, lang)}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </Link>
            <Link
              to="/booking"
              className="inline-flex items-center justify-center gap-2 border border-[#FDFBF7]/40 px-7 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#FDFBF7] hover:text-[#1A1513] transition-colors"
              data-testid="sm-cta-booking"
            >
              {pick({ en: "Send a request", fr: "Envoyer une demande", es: "Enviar una solicitud" }, lang)}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SouthernMoroccoPage;
